import { escapeHtml } from '../../../shared/utils/escapeHtml';
import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { EChartsOption } from 'echarts';
import { format } from 'date-fns';
import { BotsActivityTimeseriesResponse } from '../../../shared/types/bots-activity/BotsActivityTimeseriesResponse';
import {
  getBotColor,
  getPlatformDisplayName,
} from '../../../shared/constants/botsActivityColors';

type Granularity = 'hour' | 'day' | 'week';
type RangeOption = '1d' | '7d' | '30d' | '90d' | '365d';

const RANGE_DAYS: Record<RangeOption, number> = {
  '1d': 1,
  '7d': 7,
  '30d': 30,
  '90d': 90,
  '365d': 365,
};

const DAY_MS = 86_400_000;

/**
 * Start of the bucket containing `date`, in UTC — mirrors the backend's
 * `DATE_TRUNC(unit, hour_bucket AT TIME ZONE 'UTC')`. Week buckets start on
 * Monday (ISO), matching PostgreSQL's `DATE_TRUNC('week')`.
 */
export const truncUtc = (date: Date, granularity: Granularity): Date => {
  const dayStart = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  );
  if (granularity === 'hour') {
    dayStart.setUTCHours(date.getUTCHours());
    return dayStart;
  }
  if (granularity === 'day') return dayStart;
  const mondayOffset = (dayStart.getUTCDay() + 6) % 7;
  dayStart.setUTCDate(dayStart.getUTCDate() - mondayOffset);
  return dayStart;
};

/**
 * Full bucket axis for the selected range — independent of which buckets have
 * data. The chart maps API points onto this axis so periods with no visits
 * render as zero bars instead of disappearing from the x-axis (gap-free axis).
 */
export const buildBuckets = (range: RangeOption, granularity: Granularity): Date[] => {
  const now = new Date();
  const start = truncUtc(new Date(now.getTime() - RANGE_DAYS[range] * DAY_MS), granularity);
  const end = truncUtc(now, granularity);
  const buckets: Date[] = [];
  let current = start;
  while (current.getTime() <= end.getTime()) {
    buckets.push(current);
    const next = new Date(current);
    if (granularity === 'hour') next.setUTCHours(next.getUTCHours() + 1);
    else next.setUTCDate(next.getUTCDate() + (granularity === 'week' ? 7 : 1));
    current = next;
  }
  return buckets;
};

interface BotVisitTimeseriesChartProps {
  data: BotsActivityTimeseriesResponse | undefined;
  granularity?: Granularity;
  range?: RangeOption;
}

const formatHourLabel = (date: Date): string => format(date, 'HH:mm');
const formatDayLabel = (date: Date): string => format(date, 'd MMM');
const formatMonthLabel = (date: Date): string => format(date, 'MMM yyyy');

const formatTooltipLabel = (iso: string, granularity: Granularity): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  if (granularity === 'hour') return format(date, 'd MMM yyyy, HH:mm');
  return format(date, 'd MMM yyyy');
};

const buildAxisLabels = (
  timestamps: string[],
  granularity: Granularity,
  range: RangeOption
): string[] => {
  // Granularity drives the axis unit:
  //   hour → "HH:mm"
  //   day / week → "d MMM" (or "MMM yyyy" for 365d ranges, where day-level is too dense)
  // Every tick gets a label so the axis never looks empty; ECharts' axisLabel
  // `interval: 'auto'` + `hideOverlap` decide which to render when the chart gets dense.
  let formatLabel: (d: Date) => string;
  if (granularity === 'hour') {
    formatLabel = formatHourLabel;
  } else if (range === '365d') {
    formatLabel = formatMonthLabel;
  } else {
    formatLabel = formatDayLabel;
  }
  return timestamps.map((ts) => {
    const current = new Date(ts);
    if (Number.isNaN(current.getTime())) return ts;
    return formatLabel(current);
  });
};

const BotVisitTimeseriesChart: React.FC<BotVisitTimeseriesChartProps> = ({
  data,
  granularity = 'day',
  range = '7d',
}) => {
  const eChartsOption = useMemo((): EChartsOption | null => {
    if (!data?.timeseries || data.timeseries.length === 0) return null;

    const points = data.timeseries;
    // Aggregate by user-facing platform display name so vendor variants like
    // `OpenAI`/`ChatGPT` collapse into one series and the legend matches the
    // KPI tiles + breakdown table.
    const platformTotals: Record<string, number> = {};

    for (const point of points) {
      if (!point.platform || !point.timestamp) continue;
      const key = getPlatformDisplayName(point.platform);
      platformTotals[key] = (platformTotals[key] ?? 0) + point.count;
    }

    // Zero-fill: the axis covers every bucket in the range, not just buckets
    // present in the response — days with no data render as 0, not as gaps.
    const buckets = buildBuckets(range, granularity);
    const timestamps = buckets.map((b) => b.toISOString());
    const totalCount = Object.values(platformTotals).reduce((s, c) => s + c, 0);
    if (timestamps.length === 0 || totalCount === 0) return null;

    const sortedPlatforms = Object.entries(platformTotals)
      .sort((a, b) => b[1] - a[1])
      .map(([name]) => name);

    const seriesMap: Record<string, number[]> = {};
    for (const platform of sortedPlatforms) {
      seriesMap[platform] = new Array(timestamps.length).fill(0);
    }

    // Index buckets by their UTC start time; API timestamps are normalised
    // through truncUtc so formatting differences cannot break the lookup.
    const tsIndex = new Map(buckets.map((b, i) => [b.getTime(), i]));
    for (const point of points) {
      if (!point.platform || !point.timestamp) continue;
      const pointDate = new Date(point.timestamp);
      if (Number.isNaN(pointDate.getTime())) continue;
      const idx = tsIndex.get(truncUtc(pointDate, granularity).getTime());
      if (idx === undefined) continue;
      const key = getPlatformDisplayName(point.platform);
      if (seriesMap[key]) seriesMap[key][idx] += point.count;
    }

    const seriesNames = sortedPlatforms;
    const xAxisLabels = buildAxisLabels(timestamps, granularity, range);
    const tooltipLabels = timestamps.map((ts) => formatTooltipLabel(ts, granularity));

    return {
      grid: { top: 16, bottom: 56, left: 48, right: 16, containLabel: true },
      legend: {
        bottom: 0,
        left: 'center',
        icon: 'circle',
        itemWidth: 8,
        itemHeight: 8,
        itemGap: 16,
        textStyle: { fontSize: 12, color: '#666' },
        data: seriesNames,
      },
      xAxis: {
        type: 'category',
        data: xAxisLabels,
        axisLabel: {
          fontSize: 11,
          color: '#999',
          rotate: timestamps.length > 14 ? 45 : 0,
          interval: 'auto',
          hideOverlap: true,
        },
        axisLine: { lineStyle: { color: '#DDDDE8' } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLabel: { fontSize: 11, color: '#999' },
        splitLine: { lineStyle: { color: '#F0F0F5' } },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        borderRadius: 8,
        padding: [8, 12],
        borderColor: '#DDDDE8',
        textStyle: { fontSize: 12 },
        formatter: (params: unknown) => {
          const items = Array.isArray(params) ? params : [params];
          const headerLabel =
            items.length > 0
              ? tooltipLabels[(items[0] as { dataIndex: number }).dataIndex] ?? ''
              : '';
          const header = headerLabel
            ? `<div class="chart-tooltip__header">${escapeHtml(headerLabel)}</div>`
            : '';
          const rows = items
            .filter((p: { value: number }) => p.value > 0)
            .map(
              (p: { marker: string; seriesName: string; value: number }) =>
                `<div class="chart-tooltip__row">
                  <div class="chart-tooltip__marker">${p.marker}</div>
                  <div class="chart-tooltip__text">${escapeHtml(p.seriesName)}</div>
                  <div><strong>${escapeHtml(p.value.toLocaleString())}</strong></div>
                </div>`
            )
            .join('');
          return `<div class="chart-tooltip__content">${header}${rows}</div>`;
        },
      },
      series: seriesNames.map((name) => ({
        name,
        type: 'bar',
        stack: 'total',
        data: seriesMap[name],
        barCategoryGap: '70%',
        itemStyle: { color: getBotColor(name) },
        emphasis: { focus: 'none' },
      })),
    };
  }, [data, granularity, range]);

  if (!eChartsOption) return null;

  return (
    <ReactECharts
      option={eChartsOption}
      style={{ height: 420, width: '100%' }}
      notMerge={true}
    />
  );
};

export { BotVisitTimeseriesChart };
export default BotVisitTimeseriesChart;
